/**
 * The session token, and headers built from it.
 *
 * This exists because the app used to authenticate with `REACT_APP_API_KEY`, and
 * Create-React-App substitutes that into the bundle at build time — so the key guarding
 * uploads, deletions, the MQTT endpoints and the telemetry socket was a string literal in
 * a file every visitor downloads before logging in (verified on production 2026-09-22).
 *
 * A browser cannot keep a shared secret. It can hold a session, which is per-user, expires,
 * and can be revoked from the sessions table. The backend accepts one everywhere the key
 * used to be the only option — see `backend/endpoint_auth.py`.
 *
 * `frontend/src/noBakedSecrets.test.ts` keeps any credential-shaped `process.env` read out
 * of this source tree, so the key cannot creep back in.
 */

/** Raw session JWT, or '' when logged out. */
export function sessionToken(): string {
  return localStorage.getItem('token') || '';
}

/** `Authorization` header, omitted entirely when logged out rather than sent empty. */
export function authHeaders(contentType = false): Record<string, string> {
  const token = sessionToken();
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  if (contentType) headers['Content-Type'] = 'application/json';
  return headers;
}

/**
 * Append the session to a URL that cannot carry a header — a WebSocket, or a `<video src>`.
 *
 * Deliberately narrow: only `/ws/drone` and the phone-sensor recording stream use this.
 * A token in a URL reaches nginx access logs and browser history, so it is better than a
 * shared key rather than good — prefer a header wherever one can be sent.
 */
export function withSessionToken(url: string): string {
  const token = sessionToken();
  if (!token) return url;
  return url + (url.includes('?') ? '&' : '?') + `token=${encodeURIComponent(token)}`;
}
